import { ColorEnum } from '@/constants/color'
import { PriceItem } from '@/features/common/components/Overview/types'
import { PaymentType } from '@/features/graphql/generated/graphql'
import Button from '@/features/openTabs/components/AccountActionButton'
import CollapsibleFooter from '@/features/openTabs/components/CollapsibleFooter'
import PaymentOptionsMenu from '@/features/openTabs/components/PaymentOptionsMenu'
import { SupportedAccountPaymentMethod } from '@/features/openTabs/types'
import IconButton from '@/features/ui/buttons/components/IconButton'
import Chip from '@/features/ui/components/Chip'
import Collapsible from '@/features/ui/components/Collapsible'
import CloseIcon from '@/features/ui/icons/components/CloseIcon'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface AccountPaymentFooterProps {
    totalPrice: string
    priceItems: PriceItem[]
    paymentMethods: SupportedAccountPaymentMethod[]
    paymentMethod?: SupportedAccountPaymentMethod
    canPay: boolean
    canCalculate: boolean
    onCalculator: () => void
    errorMessage?: string
    submitting?: boolean
    onPaymentMethodChange: (method: SupportedAccountPaymentMethod) => void
    onCancel: () => void
    onPay: () => void
}

const AccountPaymentFooter: FC<AccountPaymentFooterProps> = ({
    totalPrice,
    priceItems,
    paymentMethods,
    paymentMethod,
    canPay,
    canCalculate,
    onCalculator,
    errorMessage,
    submitting = false,
    onPaymentMethodChange,
    onCancel,
    onPay,
}) => {
    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet, {
        layout: layout === 'compact' ? 'compact' : undefined,
    })
    const [showMore, setShowMore] = useState(false)
    const extraMethods = useMemo(
        () =>
            paymentMethods.filter(
                (method) => method !== PaymentType.Cash && method !== PaymentType.CardOnsite
            ),
        [paymentMethods]
    )

    useEffect(() => {
        if (!extraMethods.length) setShowMore(false)
        else if (extraMethods.some((method) => method === paymentMethod)) setShowMore(true)
    }, [extraMethods, paymentMethod])

    return (
        <CollapsibleFooter
            totalPrice={totalPrice}
            priceItems={priceItems}
            testID="open-account-payment-footer"
        >
            {errorMessage && (
                <Typography.Description size="small" style={styles.error}>
                    {errorMessage}
                </Typography.Description>
            )}
            {extraMethods.length > 0 && (
                <Collapsible open={showMore} disabled={submitting} stayExpandedWhenDisabled>
                    <View
                        aria-hidden={!showMore}
                        accessibilityElementsHidden={!showMore}
                        importantForAccessibility={showMore ? 'auto' : 'no-hide-descendants'}
                        style={styles.paymentMethods}
                    >
                        {extraMethods.map((method) => (
                            <Chip
                                key={method}
                                label={t(`openTabs.payment.paymentType.${method}`)}
                                active={paymentMethod === method}
                                disabled={submitting}
                                onPress={() => onPaymentMethodChange(method)}
                                style={styles.paymentMethod}
                            />
                        ))}
                    </View>
                </Collapsible>
            )}
            <View style={styles.paymentMethods}>
                <Chip
                    label={t(`openTabs.payment.paymentType.${PaymentType.Cash}`)}
                    active={paymentMethod === PaymentType.Cash}
                    disabled={submitting || !paymentMethods.includes(PaymentType.Cash)}
                    onPress={() => onPaymentMethodChange(PaymentType.Cash)}
                    style={styles.paymentMethod}
                />
                <Chip
                    label={t(`openTabs.payment.paymentType.${PaymentType.CardOnsite}`)}
                    active={paymentMethod === PaymentType.CardOnsite}
                    disabled={submitting || !paymentMethods.includes(PaymentType.CardOnsite)}
                    onPress={() => onPaymentMethodChange(PaymentType.CardOnsite)}
                    style={styles.paymentMethod}
                />
                <Chip
                    label={t(showMore ? 'openTabs.payment.less' : 'openTabs.payment.more')}
                    active={extraMethods.some((method) => method === paymentMethod)}
                    disabled={submitting || !extraMethods.length}
                    onPress={() => setShowMore((current) => !current)}
                    style={styles.paymentMethod}
                />
            </View>
            <View style={styles.actions}>
                <IconButton
                    Icon={CloseIcon}
                    color="primary"
                    type="filled"
                    size={44}
                    disabled={submitting}
                    accessibilityLabel={t('openTabs.actions.cancel')}
                    onPress={onCancel}
                    style={styles.cancelButton}
                    testID="open-account-payment-cancel"
                />
                <Button
                    color="action"
                    disabled={
                        !canPay ||
                        !paymentMethod ||
                        !paymentMethods.includes(paymentMethod) ||
                        submitting
                    }
                    onPress={onPay}
                    style={styles.button}
                    testID="open-account-payment-confirm"
                >
                    {t('openTabs.actions.payAndFinish')}
                </Button>
                <PaymentOptionsMenu
                    disabled={!canCalculate || submitting}
                    onCalculator={onCalculator}
                />
            </View>
        </CollapsibleFooter>
    )
}

const stylesheet = createStyleSheet({
    error: {
        color: ColorEnum.GRAY_600,
    },
    paymentMethods: {
        flexDirection: 'row',
        gap: 8,
    },
    paymentMethod: {
        flex: 1,
        minHeight: 40,
        borderRadius: 8,
        variants: {
            layout: {
                compact: {
                    minHeight: 44,
                    paddingHorizontal: 4,
                },
            },
        },
    },
    actions: {
        width: '100%',
        flexDirection: 'row',
        gap: 16,
    },
    cancelButton: { borderRadius: 8 },
    button: {
        flex: 1,
        height: 44,
        justifyContent: 'center',
        borderRadius: 8,
    },
})

export default AccountPaymentFooter
